import { useDeleteSimulator } from "@/api/useRelease";
import { Dialog } from "@/components/shared/dialog";
import { useReleases } from "@/routes/simulator/context/release";
import { useEffect, useMemo } from "react";
import styles from "./styles.module.scss";
import { CrossCircledIcon } from "@radix-ui/react-icons";
import { mixTrackEvent, SimulatorEvents } from "@/assets/mixpanel";
import { Button } from "@radix-ui/themes";

interface DeleteSimulatorDialogProps {
  open: boolean;
  simulatorId: number;
  simulatorName: string;
  onClose: () => void;
}

function isError(data: any) {
  return data.error != null && data.statusCode != null && data.message != null;
}

export function DeleteSimulatorDialog({
  open,
  simulatorId,
  simulatorName,
  onClose,
}: DeleteSimulatorDialogProps) {
  const { refetchSimulators } = useReleases();
  const { mutate, isLoading, data, error } = useDeleteSimulator({
    simulatorId,
  });

  const erroMessage = useMemo<string>(() => {
    if (data != null && isError(data)) {
      return data.message;
    } else if (error != null) {
      return (error as { message: string }).message;
    }
  }, [data, error]);

  useEffect(() => {
    if (data != null && !isError(data)) {
      refetchSimulators().then(() => {
        onClose();
      });
    }
  }, [data, onClose, refetchSimulators]);

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Dialog.Content size={"small"}>
        <Dialog.Title>
          Delete AI Instance
          <Dialog.Close />
        </Dialog.Title>

        <div className={styles.DeleteSimulatorDialog}>
          <div className={styles.Content}>
            Are you sure you want to delete "{simulatorName}" ?
          </div>

          {erroMessage != null && (
            <div className={styles.ErroMessage}>
              <CrossCircledIcon />
              {erroMessage}
            </div>
          )}

          <div className={styles.Footer}>
            <Button
              color={"red"}
              variant={"solid"}
              size={"1"}
              loading={isLoading}
              disabled={erroMessage != null}
              onClick={() => {
                mutate({});
                mixTrackEvent({
                  event: SimulatorEvents.DELETED,
                  properties: {
                    id: simulatorId,
                  },
                });
              }}
            >
              Delete
            </Button>

            <Button
              color={"gray"}
              variant={"ghost"}
              size={"1"}
              disabled={isLoading}
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
